import React, { useState, useEffect } from 'react';
import { logisticsAPI } from '../api';
import { extractErrorMessage, withRetry } from '../utils/errorHandling';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import { formatCurrency } from '../utils/formatNumber';
import type { 
  ShipmentBookingData, 
  RateOption, 
  RateRequest,
  Shipment 
} from '../types/logistics';

interface ShipmentBookingFormProps {
  onSuccess: (shipment: Shipment) => void;
  onCancel: () => void;
}

interface FormErrors {
  [key: string]: string;
}

const ShipmentBookingForm: React.FC<ShipmentBookingFormProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<ShipmentBookingData>({
    provider: '', senderName: '', senderAddress: '', senderCity: '', senderState: '',
    senderPincode: '', senderPhone: '', recipientName: '', recipientAddress: '',
    recipientCity: '', recipientState: '', recipientPincode: '', recipientPhone: '',
    weight: 0, length: 0, width: 0, height: 0, declaredValue: undefined,
  });

  const [rates, setRates] = useState<RateOption[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [ratesCalculated, setRatesCalculated] = useState(false);

  useEffect(() => {
    const { weight, length, width, height, recipientPincode, senderPincode } = formData;
    if (weight > 0 && length > 0 && width > 0 && height > 0 &&
        recipientPincode && senderPincode &&
        recipientPincode.length >= 6 && senderPincode.length >= 6) {
      calculateRates();
    } else {
      setRates([]); setRatesCalculated(false); setSelectedProvider('');
    }
  }, [formData.weight, formData.length, formData.width, formData.height,
      formData.recipientPincode, formData.senderPincode]);

  const calculateRates = async () => {
    setRatesLoading(true); setError(null);
    try {
      const rateRequest: RateRequest = {
        originPincode: formData.senderPincode, destinationPincode: formData.recipientPincode,
        weight: formData.weight, length: formData.length, width: formData.width,
        height: formData.height, declaredValue: formData.declaredValue || null,
      };
      const response = await withRetry(() => logisticsAPI.calculateRates(rateRequest), { maxAttempts: 2 });
      const sorted = (response.data as RateOption[]).sort((a, b) => a.rate - b.rate);
      setRates(sorted); setRatesCalculated(true); setSelectedProvider('');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to calculate shipping rates'));
      setRates([]); setRatesCalculated(false);
    } finally { setRatesLoading(false); }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const required = [
      'senderName', 'senderAddress', 'senderCity', 'senderState', 'senderPincode', 'senderPhone',
      'recipientName', 'recipientAddress', 'recipientCity', 'recipientState', 'recipientPincode', 'recipientPhone'
    ];
    required.forEach(f => { if (!formData[f as keyof ShipmentBookingData]) newErrors[f] = 'Required'; });
    if (formData.weight <= 0) newErrors.weight = 'Must be > 0';
    if (formData.length <= 0) newErrors.length = 'Must be > 0';
    if (formData.width <= 0) newErrors.width = 'Must be > 0';
    if (formData.height <= 0) newErrors.height = 'Must be > 0';
    if (formData.senderPincode && !/^\d{6}$/.test(formData.senderPincode)) newErrors.senderPincode = 'Must be 6 digits';
    if (formData.recipientPincode && !/^\d{6}$/.test(formData.recipientPincode)) newErrors.recipientPincode = 'Must be 6 digits';
    if (formData.senderPhone && !/^\d{10}$/.test(formData.senderPhone.replace(/\D/g, ''))) newErrors.senderPhone = '10 digits required';
    if (formData.recipientPhone && !/^\d{10}$/.test(formData.recipientPhone.replace(/\D/g, ''))) newErrors.recipientPhone = '10 digits required';
    if (!selectedProvider) newErrors.provider = 'Select a shipping provider';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ShipmentBookingData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleProviderSelect = (provider: string) => {
    setSelectedProvider(provider);
    setFormData(prev => ({ ...prev, provider }));
    if (errors.provider) setErrors(prev => { const n = { ...prev }; delete n.provider; return n; });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true); setError(null);
    try {
      const bookingData: ShipmentBookingData = { ...formData, provider: selectedProvider };
      const response = await withRetry(() => logisticsAPI.bookShipment(bookingData), { maxAttempts: 2 });
      onSuccess(response.data as Shipment);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to book shipment'));
    } finally { setLoading(false); }
  };

  const fieldInput = (label: string, field: keyof ShipmentBookingData, placeholder: string, opts?: { type?: string; maxLength?: number; step?: string; min?: string }) => (
    <div className="booking-field">
      <label>{label}</label>
      {field === 'senderAddress' || field === 'recipientAddress' ? (
        <textarea value={formData[field] as string} onChange={e => handleInputChange(field, e.target.value)}
          className={errors[field] ? 'has-error' : ''} placeholder={placeholder} />
      ) : (
        <input type={opts?.type || 'text'} value={formData[field] as string | number || ''}
          onChange={e => handleInputChange(field, opts?.type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          className={errors[field] ? 'has-error' : ''} placeholder={placeholder}
          maxLength={opts?.maxLength} step={opts?.step} min={opts?.min} />
      )}
      {errors[field] && <div className="booking-field-error">{errors[field]}</div>}
    </div>
  );

  return (
    <div className="booking-overlay">
      <div className="booking-modal">
        {/* Header */}
        <div className="booking-header">
          <h3 className="booking-title">📦 Create New Shipment</h3>
          <button className="booking-close" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Sender / Recipient grid */}
          <div className="booking-body">
            <div>
              <h4 className="booking-section-title">📤 Sender Information</h4>
              <div className="booking-fields">
                {fieldInput('Name *', 'senderName', 'Enter sender name')}
                {fieldInput('Address *', 'senderAddress', 'Enter complete address')}
                <div className="booking-field-row">
                  {fieldInput('City *', 'senderCity', 'City')}
                  {fieldInput('State *', 'senderState', 'State')}
                </div>
                <div className="booking-field-row">
                  {fieldInput('Pincode *', 'senderPincode', '123456', { maxLength: 6 })}
                  {fieldInput('Phone *', 'senderPhone', '9876543210', { type: 'tel' })}
                </div>
              </div>
            </div>

            <div>
              <h4 className="booking-section-title">📥 Recipient Information</h4>
              <div className="booking-fields">
                {fieldInput('Name *', 'recipientName', 'Enter recipient name')}
                {fieldInput('Address *', 'recipientAddress', 'Enter complete address')}
                <div className="booking-field-row">
                  {fieldInput('City *', 'recipientCity', 'City')}
                  {fieldInput('State *', 'recipientState', 'State')}
                </div>
                <div className="booking-field-row">
                  {fieldInput('Pincode *', 'recipientPincode', '123456', { maxLength: 6 })}
                  {fieldInput('Phone *', 'recipientPhone', '9876543210', { type: 'tel' })}
                </div>
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="booking-package-section">
            <h4 className="booking-section-title" style={{ marginTop: '20px' }}>📦 Package Details</h4>
            <div className="booking-package-grid">
              {fieldInput('Weight (kg) *', 'weight', '2.5', { type: 'number', step: '0.1', min: '0.1' })}
              {fieldInput('Length (cm) *', 'length', '30', { type: 'number', step: '0.1', min: '0.1' })}
              {fieldInput('Width (cm) *', 'width', '20', { type: 'number', step: '0.1', min: '0.1' })}
              {fieldInput('Height (cm) *', 'height', '15', { type: 'number', step: '0.1', min: '0.1' })}
              {fieldInput('Declared Value (₹)', 'declaredValue', '1000', { type: 'number', step: '0.01', min: '0' })}
            </div>
          </div>

          {/* Rate Options */}
          {(ratesLoading || rates.length > 0 || (ratesCalculated && rates.length === 0)) && (
            <div className="booking-rates-section">
              <h4 className="booking-section-title" style={{ marginTop: '20px' }}>💰 Rate Options</h4>
              {ratesLoading && (
                <div className="booking-rates-loading"><LoadingSpinner size="medium" message="Calculating shipping rates..." /></div>
              )}
              {!ratesLoading && rates.length === 0 && ratesCalculated && (
                <div className="booking-rates-empty">No shipping rates available for this route. Please check the pincode details.</div>
              )}
              {!ratesLoading && rates.length > 0 && (
                <div className="booking-rate-list">
                  {rates.map((rate, index) => (
                    <div key={`${rate.provider}-${index}`}
                      className={`booking-rate-card${selectedProvider === rate.provider ? ' selected' : ''}`}
                      onClick={() => handleProviderSelect(rate.provider)}>
                      <div className="booking-rate-left">
                        <input type="radio" name="provider" checked={selectedProvider === rate.provider}
                          onChange={() => handleProviderSelect(rate.provider)} />
                        <span className={`logistics-provider-badge provider-${rate.provider.toLowerCase()}`}>
                          {rate.provider.toUpperCase()}
                        </span>
                        <div>
                          <div className="booking-rate-service">{rate.serviceType.replace('_', ' ').toUpperCase()}</div>
                          <div className="booking-rate-delivery">
                            Estimated delivery: {rate.estimatedDeliveryDays} day{rate.estimatedDeliveryDays !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="booking-rate-price">{formatCurrency(rate.rate)}</div>
                        <div className="booking-rate-currency">{rate.currency}</div>
                      </div>
                    </div>
                  ))}
                  {errors.provider && <div className="booking-field-error">{errors.provider}</div>}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '0 24px 16px' }}>
              <ErrorDisplay error={error}
                onRetry={() => { setError(null); if (ratesCalculated) calculateRates(); }}
                onDismiss={() => setError(null)} />
            </div>
          )}

          {/* Footer */}
          <div className="booking-footer">
            <button type="button" className="booking-btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
            <button type="submit" className="booking-btn-submit" disabled={loading || !selectedProvider}>
              {loading ? (<><div className="booking-btn-spinner" /> Booking...</>) : (<>📦 Book Shipment</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShipmentBookingForm;
